/**
 * Backend ageValidation format: "<Number" or ">Number", combined with "&" when multiple.
 * Examples: "<30", ">18", "<30&>18" (age must be < 30 AND > 18).
 */

export interface AgeCondition {
  op: '<' | '>'
  value: number
}

/**
 * Parses the ageValidation string into an array of conditions.
 * Returns null if the string is empty, invalid, or not a known format.
 */
export function parseAgeValidation(
  ageValidation: string | null | undefined
): AgeCondition[] | null {
  const trimmed = ageValidation?.trim()
  if (!trimmed) return null

  const parts = trimmed.split('&').map((s) => s.trim())
  const conditions: AgeCondition[] = []

  for (const part of parts) {
    const match = part.match(/^(<|>)(\d+)$/)
    if (!match) return null
    const value = parseInt(match[2], 10)
    if (Number.isNaN(value)) return null
    conditions.push({ op: match[1] as '<' | '>', value })
  }

  return conditions.length > 0 ? conditions : null
}

/**
 * Returns true if the given exact age satisfies all conditions in the ageValidation string.
 * If ageValidation is empty or invalid, returns true (no restriction).
 */
export function isAgeInScope(
  exactAge: number,
  ageValidation: string | null | undefined
): boolean {
  const conditions = parseAgeValidation(ageValidation)
  if (!conditions) return true

  return conditions.every(({ op, value }) =>
    op === '<' ? exactAge < value : exactAge > value
  )
}

/**
 * Returns a short human-readable description of the age rule for error messages.
 * e.g. "<30&>18" -> "zwischen 19 und 29 Jahren", "<30" -> "unter 30 Jahren"
 */
export function formatAgeValidationForMessage(
  ageValidation: string | null | undefined
): string {
  const conditions = parseAgeValidation(ageValidation)
  if (!conditions || conditions.length === 0) return ''

  const gt = conditions.find((c) => c.op === '>')
  const lt = conditions.find((c) => c.op === '<')

  if (gt && lt) {
    return `zwischen ${gt.value + 1} und ${lt.value - 1} Jahren`
  }
  if (lt) {
    return `unter ${lt.value} Jahren`
  }
  if (gt) {
    return `über ${gt.value} Jahren`
  }
  return ''
}
